import React, { useEffect, useRef, useState } from "react";
import { Wallet, Copy, Trash2, History, FileText } from "lucide-react";
import { cn } from "../../../../utils/classnames";
import Input from "../../../../components/ui/inputs/Input";
import CustomSelect from "../../../../components/ui/selects/CustomSelect";
import CustomButton from "../../../../components/ui/buttons/CustomButton";
import WalletAssets from "./WalletAssets";
import { useToast } from "../../../../components/ui/toast/ToastContext";

function formatPercentage(value) {
  const number = Number(value) || 0;

  return `${number.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

const getPolicyImportFromWallet = (wallet) => {
  return (
    wallet?.policyImport ||
    wallet?.policy_import ||
    wallet?.policyFile ||
    wallet?.importedPolicyFile ||
    wallet?.investmentPolicyFile ||
    wallet?.investmentPolicyImport ||
    wallet?.latestPolicyImport ||
    null
  );
};

const getPolicyImportFromResult = (result) => {
  return (
    result?.policyImport ||
    result?.policy_import ||
    result?.policyFile ||
    result?.policy_file ||
    result?.investmentPolicyImport ||
    result?.investment_policy_import ||
    null
  );
};

const normalizeErrorMessage = (
  error,
  fallback = "Ocorreu um erro inesperado.",
) => {
  if (error?.errors?.length) return error.errors.join(", ");
  if (error?.error) return error.error;
  if (error?.message) return error.message;

  return fallback;
};

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content;

const WalletEditor = ({
  currentWallet,
  currentWalletForm,
  walletProfileOptions,
  totalAllocation,
  isTotalComplete,
  loading,
  highlightId,
  onChangeField,
  onChangeAssetAllocation,
  onChangeFixedIncome,
  onRemoveAsset,
  onReplaceAsset,
  onRemoveComplementaryAsset,
  onAddComplementaryAsset,
  onAddAsset,
  onDiscardChanges,
  onSave,
  onDuplicate,
  onDelete,
  onImportPolicy,
  walletShore,
}) => {
  const fileInputRef = useRef(null);
  const { addToast } = useToast();

  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importingPolicy, setImportingPolicy] = useState(false);
  const [deletingPolicyImport, setDeletingPolicyImport] = useState(false);
  const [policyImport, setPolicyImport] = useState(() =>
    getPolicyImportFromWallet(currentWallet),
  );

  useEffect(() => {
    setPolicyImport(getPolicyImportFromWallet(currentWallet));
  }, [currentWallet]);

  const hasPolicyImport = Boolean(policyImport);

  const policyImportTitle =
    policyImport?.title || `Planilha - Carteira ${currentWallet?.name || ""}`;

  const policyImportFileName =
    policyImport?.originalFilename || "importada manualmente";

  const policyImportUploadedAt =
    policyImport?.uploadedAt ||
    policyImport?.uploaded_at ||
    policyImport?.createdAt ||
    policyImport?.created_at ||
    null;

  const formatPolicyDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return String(dateValue);

    return date.toLocaleDateString("pt-BR");
  };

  const handleChooseImportFile = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedImportFile(null);
      return;
    }

    const allowedExtensions = ["csv", "xlsx", "xls"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      setSelectedImportFile(null);
      event.target.value = "";

      addToast({
        type: "error",
        message:
          "Formato de arquivo inválido. Envie um arquivo csv, xlsx, xls.",
      });

      return;
    }

    setSelectedImportFile(file);
  };

  const handleSubmitImportPolicy = async () => {
    if (!selectedImportFile) {
      addToast({
        type: "error",
        message: "Selecione um arquivo para importar.",
      });

      return;
    }

    setImportingPolicy(true);

    try {
      const result = await onImportPolicy(selectedImportFile);
      const nextPolicyImport = getPolicyImportFromResult(result) || {
        title: `Planilha - Carteira ${currentWallet?.name || ""}`,
        filename: "importada manualmente",
        uploadedAt: new Date().toISOString(),
      };

      setPolicyImport(nextPolicyImport);
      setSelectedImportFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      addToast({
        type: "success",
        message: "Histórico da carteira importado com sucesso.",
      });
    } catch (error) {
      addToast({
        type: "error",
        message: normalizeErrorMessage(
          error,
          "Erro ao importar histórico da carteira.",
        ),
      });
    } finally {
      setImportingPolicy(false);
    }
  };

  const handleDeletePolicyImport = async () => {
    if (!currentWallet?.id) {
      addToast({
        type: "error",
        message: "Carteira não encontrada.",
      });

      return;
    }

    setDeletingPolicyImport(true);

    try {
      const response = await fetch(
        `/app/analyst/wallet_configs/wallets/${currentWallet.id}/import_policy`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
        },
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw responseData;
      }

      setPolicyImport(null);
      setSelectedImportFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      addToast({
        type: "success",
        message: "Histórico da carteira excluído com sucesso.",
      });
    } catch (error) {
      addToast({
        type: "error",
        message: normalizeErrorMessage(
          error,
          "Erro ao excluir histórico da carteira.",
        ),
      });
    } finally {
      setDeletingPolicyImport(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-row items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="text-tealMedium" size={20} strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-blueDarker">
            Editar Carteira de Investimento:
          </h1>
          <div className="bg-blueLight px-3 py-1 text-xl text-blueDarker font-normal flex items-center gap-2 rounded-lg">
            <span className="font-normal">
              {currentWallet.walletProfile?.name}
            </span>
            <span>-</span>
            <span className="font-semibold">{currentWallet.name}</span>
          </div>
        </div>

        <div className="flex items-center gap-5">
          <button
            type="button"
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 disabled:opacity-50"
            onClick={onDuplicate}
            disabled={loading}
          >
            <Copy size={16} className="text-blueHighlight" />
            <span className="text-blueHighlight font-semibold text-sm underline">
              Duplicar Carteira
            </span>
          </button>

          <button
            type="button"
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 disabled:opacity-50"
            onClick={onDelete}
            disabled={loading}
          >
            <Trash2 size={16} className="text-redBase" />
            <span className="text-redBase font-semibold text-sm underline">
              Excluir Carteira
            </span>
          </button>
        </div>
      </div>

      {/* Formulário Principal */}
      <div className="grid grid-cols-12 gap-5 w-full items-start pr-2">
        <div className="col-span-3">
          <Input
            name="name"
            label="Nome da Carteira"
            value={currentWalletForm.name || ""}
            onChange={onChangeField}
          />
        </div>

        <div className="col-span-3">
          <CustomSelect
            name="walletProfileId"
            label="Perfil de Carteira"
            options={walletProfileOptions.map((profile) => ({
              label: profile.name,
              value: profile.id,
              colorHex: profile.colorHex,
            }))}
            value={currentWalletForm.walletProfileId || ""}
            onChange={onChangeField}
            disabled={walletProfileOptions.length === 0 || loading}
          />
        </div>

        <div className="col-span-3">
          <CustomSelect
            name="walletType"
            label="Tipo de Carteira"
            options={
              walletShore === "NACIONAL"
                ? [
                  { label: "Investimento", value: "investment" },
                  { label: "Previdência", value: "pension" },
                ]
                : [{ label: "Investimento", value: "investment" }]
            }
            value={currentWalletForm.walletType || ""}
            onChange={onChangeField}
          />
        </div>

        <div className="col-span-3">
          <Input
            name="maxVolatility"
            label="Volatilidade máxima (%)"
            type="number"
            value={Number(currentWalletForm.maxVolatility || 0)}
            onChange={onChangeField}
          />
        </div>
      </div>

      {/* Histórico / Upload da planilha */}
      {currentWallet.walletType === "investment" && (
        <div className="flex flex-col gap-5 pr-2">
          <div className="flex flex-row items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <History
                className="text-tealMedium"
                size={20}
                strokeWidth={1.5}
              />
              <h1 className="text-2xl font-semibold text-blueDarker">
                Histórico da Carteira
              </h1>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5 items-start">
            <div className="col-span-6">
              {hasPolicyImport ? (
                <div
                  className="bg-blueLight flex items-center justify-between gap-4 px-5"
                  style={{ borderRadius: 12, minHeight: 64 }}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className="bg-white rounded-full flex items-center justify-center shrink-0"
                      style={{ width: 40, height: 40 }}
                    >
                      <FileText
                        className="text-tealMedium"
                        size={22}
                        strokeWidth={1.5}
                      />
                    </div>

                    <div className="min-w-0">
                      <h3
                        className="text-base font-semibold text-blueDarker truncate"
                        title={policyImportTitle}
                      >
                        {policyImportTitle}
                      </h3>

                      <p
                        className="text-sm italic text-blueSteel truncate"
                        title={policyImportFileName}
                      >
                        {policyImportFileName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-8 shrink-0">
                    {policyImportUploadedAt && (
                      <div className="text-center leading-tight">
                        <p className="text-sm italic text-blueSteel">
                          Upload em:
                        </p>
                        <p className="text-base font-semibold text-blueSteel">
                          {formatPolicyDate(policyImportUploadedAt)}
                        </p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleDeletePolicyImport}
                      disabled={
                        loading || importingPolicy || deletingPolicyImport
                      }
                      className="text-redSoft font-semibold text-sm underline hover:opacity-80 disabled:opacity-50"
                    >
                      {deletingPolicyImport ? "Excluindo..." : "Excluir"}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="bg-blueLight flex items-center px-5 text-sm text-blueSteel"
                  style={{ borderRadius: 12, minHeight: 64 }}
                >
                  Nenhum histórico importado.
                </div>
              )}
            </div>

            <div className="col-span-6">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={handleImportFileChange}
                    disabled={
                      loading || importingPolicy || deletingPolicyImport
                    }
                  />

                  <button
                    type="button"
                    onClick={handleChooseImportFile}
                    disabled={
                      loading || importingPolicy || deletingPolicyImport
                    }
                    className={cn(
                      "w-full px-5 rounded-xl border border-bluePale bg-white text-left text-base",
                      "text-blueSteel hover:bg-blueUltraLight transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                    )}
                    style={{ height: 44 }}
                  >
                    <span className="block truncate">
                      {selectedImportFile?.name || "Escolher arquivo..."}
                    </span>
                  </button>

                  <p className="text-sm text-blueSteel mt-2">
                    <span className="font-semibold">Arquivos suportados:</span>{" "}
                    csv, xlsx, xls.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleSubmitImportPolicy}
                  disabled={
                    loading ||
                    importingPolicy ||
                    deletingPolicyImport ||
                    !selectedImportFile
                  }
                  className={cn(
                    "bg-tealMedium text-white text-sm font-semibold rounded-xl px-5 transition-colors shrink-0 border-r-2 border-b-2 border-tealBase",
                    loading ||
                      importingPolicy ||
                      deletingPolicyImport ||
                      !selectedImportFile
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-tealDark",
                  )}
                  style={{ height: 44, minWidth: 116 }}
                >
                  {importingPolicy ? "IMPORTANDO..." : "ADICIONAR"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assets */}
      <div className="min-h-0 custom-scrollbar pr-2 overflow-y-auto justify-center flex">
        <WalletAssets
          assetsAttributes={currentWalletForm.walletAssetsAttributes}
          walletType={currentWalletForm.walletType}
          fixedIncomePercentage={currentWalletForm.fixedIncomePercentage}
          highlightId={highlightId}
          onChangeFixedIncome={onChangeFixedIncome}
          onRemoveAsset={onRemoveAsset}
          onChangeAllocation={onChangeAssetAllocation}
          onReplaceAsset={onReplaceAsset}
          onRemoveComplementaryAsset={onRemoveComplementaryAsset}
          onAddComplementaryAsset={onAddComplementaryAsset}
        />
      </div>

      {/* Botões de Ação Final */}
      <div className="flex justify-between mt-auto pt-6">
        <CustomButton
          onClick={onAddAsset}
          fontSize="text-sm"
          bgColor="bg-blueHighlight"
          borderColor="border-blueMediumDark2"
        >
          + ADICIONAR ATIVO
        </CustomButton>

        <div className="flex gap-3">
          <div
            className={cn(
              "px-4 py-1.5 text-base flex items-center gap-2 rounded-xl transition-colors duration-300",
              isTotalComplete
                ? "bg-tealExtraLight2 text-tealMedium border border-tealMedium"
                : "bg-redExtraLight text-redBase border border-redBase",
            )}
          >
            <span className="font-normal">Carteira fechando em:</span>
            <span className="font-bold">
              {totalAllocation.toFixed(1).replace(".", ",")}%
            </span>
          </div>

          <CustomButton
            onClick={onDiscardChanges}
            fontSize="text-sm"
            bgColor="bg-blueUltraLight"
            borderColor="border-graySemiDark"
            className="text-grayDark"
          >
            DESCARTAR MUDANÇAS
          </CustomButton>

          <CustomButton onClick={onSave} fontSize="text-sm" disabled={loading}>
            SALVAR ALTERAÇÕES
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default WalletEditor;
